/**
 * Retry utility for JsonRPC service calls.
 *
 * Retries a failing API call when the error is a 503 Service Unavailable
 * response or a connection timeout, using exponential backoff.
 *
 * Error shape produced by TransportService:
 *   { baseError, errorCode, appErrorCode, isTimeout, isUnauthenticated }
 */

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;

/**
 * Returns true when the TransportService/JsonRPCService error should trigger a retry.
 * @param {Object} error
 * @returns {boolean}
 */
const isRetryableError = (error) => {
    if (!error) {return false;}

    // Connection timeout: TransportService sets isTimeout=true for fetch errors (status 0) < 90 s
    if (error.isTimeout === true) {return true;}

    // 503 Service Unavailable: reflected in baseError message from TransportService
    if (typeof error.baseError === 'string' && error.baseError.includes('503')) {return true;}

    return false;
};

const delay = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * Wraps a function that returns a Promise with automatic retry logic.
 * Retries up to MAX_RETRIES times with exponential backoff on 503 or timeout errors.
 *
 * @param {Function} fn              - Zero-argument function returning a Promise
 * @param {number}   [retriesLeft]   - Remaining retry attempts (default: MAX_RETRIES)
 * @param {number}   [delayMs]       - Delay before the next attempt in ms (doubles each retry)
 * @returns {Promise}
 */
export const withRetry = (fn, retriesLeft = MAX_RETRIES, delayMs = BASE_DELAY_MS) => fn().catch((error) => {
        if (retriesLeft > 0 && isRetryableError(error)) {
            return delay(delayMs).then(() => withRetry(fn, retriesLeft - 1, delayMs * 2));
        }

        return Promise.reject(error);
    });
